import {inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, finalize, map, Observable, of, shareReplay, tap} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {
  IHairstyle,
  IHairstyleFilters,
  IHairstyleResponse,
  IPageHairstyleResponse,
  IWebsiteHairstyle,
  IWebsiteHairstylesResponse
} from '../../interfaces/hairstyle.interface';
import {ToastrService} from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class HairstylesService {
  readonly #http: HttpClient = inject(HttpClient);
  readonly #toastrService: ToastrService = inject(ToastrService);
  readonly #apiBaseUrl: string = environment.apiUrl;

  #isLoading: WritableSignal<boolean> = signal(false);
  #isSaving: WritableSignal<boolean> = signal(false);
  #isDeleting: WritableSignal<boolean> = signal(false);
  #hairstyles: WritableSignal<IHairstyle[]> = signal<IHairstyle[]>([]);
  #hairstylesPaginated: WritableSignal<IPageHairstyleResponse | null> = signal<IPageHairstyleResponse | null>(null);
  #hairstyle: WritableSignal<IHairstyle | null> = signal<IHairstyle | null>(null);

  hairstyles: Signal<IHairstyle[]> = this.#hairstyles.asReadonly();
  hairstylesPaginated: Signal<IPageHairstyleResponse | null> = this.#hairstylesPaginated.asReadonly();
  hairstyle: Signal<IHairstyle | null> = this.#hairstyle.asReadonly();
  isLoading: Signal<boolean> = this.#isLoading.asReadonly();
  isSaving: Signal<boolean> = this.#isSaving.asReadonly();
  isDeleting: Signal<boolean> = this.#isDeleting.asReadonly();
  page: WritableSignal<number> = signal(1);
  filters: WritableSignal<IHairstyleFilters | null> = signal<IHairstyleFilters | null>(null);

  getHairstyles(): Observable<IHairstyleResponse> {
    this.#isLoading.set(true);
    return this.#http.get<IHairstyleResponse>(`${this.#apiBaseUrl}/tools/coiffure/all`).pipe(
      shareReplay(1),
      tap((res: IHairstyleResponse) => this.#hairstyles.set(res.listCoiffure)),
      finalize(() => this.#isLoading.set(false))
    );
  }

  getPaginatedHairstyles(): Observable<IPageHairstyleResponse> {
    this.#isLoading.set(true);
    let params: HttpParams = new HttpParams().set('page', this.page()).set('limit', '12');
    if (this.filters()) {
      if (this.filters()?.nom) {
        params = params.set('nom', this.filters()!.nom!);
      }
      if (this.filters()?.prix_moy) {
        params = params.set('prix_moy', this.filters()!.prix_moy!);
      }
    }
    return this.#http.get<IPageHairstyleResponse>(`${this.#apiBaseUrl}/hairstyles`, {params}).pipe(
      tap((res: IPageHairstyleResponse): void => {
        this.#hairstylesPaginated.set(res);
      }),
      catchError((error: any) => {
        throw error;
      }),
      finalize(() => this.#isLoading.set(false))
    );
  }

  GetWebSiteHairstyleList(): Observable<IWebsiteHairstyle[]> {
    this.#isLoading.set(true);
    return this.#http.get<IWebsiteHairstylesResponse>(`${this.#apiBaseUrl}/tools/coiffure/all`).pipe(
      map((response: IWebsiteHairstylesResponse) => {
        return response.listCoiffure || [];
      }),
      catchError(error => {
        this.#toastrService.error('Erreur lors du chargement des coiffures', 'Erreur');
        console.error('Error loading website hairstyles:', error);
        return of([]);
      }),
      finalize(() => this.#isLoading.set(false))
    );
  }

  PostHairstyle(serviceData: any): Observable<any> {
    this.#isSaving.set(true);
    return this.#http.post(`${this.#apiBaseUrl}/tools/coiffure`, serviceData).pipe(
      tap(() => {
        this.#toastrService.success('Service added to your list!', 'Success');
      }),
      catchError(error => {
        this.#toastrService.error('Failed to add service. Please try again.', 'Error');
        throw error;
      }),
      finalize(() => this.#isSaving.set(false))
    );
  }

  PostHairstyleFromCatalog(serviceData: FormData): Observable<any> {
    this.#isSaving.set(true);
    return this.#http.post(`${this.#apiBaseUrl}/tools/coiffure`, serviceData).pipe(
      tap(() => {
        this.#toastrService.success('Service ajouté depuis le catalogue!', 'Succès');
      }),
      catchError(error => {
        this.#toastrService.error('Erreur lors de l\'ajout du service', 'Erreur');
        throw error;
      }),
      finalize(() => this.#isSaving.set(false))
    );
  }

  deleteImageHairstyle(hairstyleId: string, imageLink: string): Observable<any> {
    const encodedImageLink = encodeURIComponent(imageLink);
    const url = `${this.#apiBaseUrl}/tools/coiffure/images?hairstyleId=${hairstyleId}&imageLink=${encodedImageLink}`;

    return this.#http.delete(url).pipe(
      tap(() => {
        this.#toastrService.success('Image deleted successfully!', 'Success');
      }),
      catchError(error => {
        this.#toastrService.error('Failed to delete image. Please try again.', 'Error');
        throw error;
      })
    );
  }

  UpdateImageHairstyle(formData: any): Observable<any> {
    this.#isSaving.set(true);
    return this.#http.post(`${this.#apiBaseUrl}/tools/coiffure/images`, formData).pipe(
      tap(() => {
        this.#toastrService.success('Service images updated successfully!', 'Success');
      }),
      catchError(error => {
        this.#toastrService.error('Failed to update images. Please try again.', 'Error');
        throw error;
      }),
      finalize(() => this.#isSaving.set(false))
    );
  }

  PostNewHairstyle(formData: FormData): Observable<any> {
    this.#isSaving.set(true);
    return this.#http.post(`${this.#apiBaseUrl}/tools/coiffure/new`, formData).pipe(
      tap(() => {
        this.#toastrService.success('Service created successfully!', 'Success');
      }),
      catchError(error => {
        this.#toastrService.error('Failed to create service. Please try again.', 'Error');
        throw error;
      }),
      finalize(() => this.#isSaving.set(false))
    );
  }

  /*PostNewHairstyle(formData: any) {
    return this.#http.post(`${this.#apiBaseUrl}/tools/coiffure/new`, formData);
  }*/

  deleteHairstyle(id: number): Observable<any> {
    this.#isDeleting.set(true);
    return this.#http.delete(`${this.#apiBaseUrl}/tools/coiffure?id=${id}`).pipe(
      tap(() => {
        this.#toastrService.success('Service deleted successfully!', 'Success');
        // Mettre à jour la liste locale en supprimant l'élément
        const currentHairstyles = this.#hairstyles();
        const updatedHairstyles = currentHairstyles.filter(hairstyle => hairstyle.id !== id);
        this.#hairstyles.set(updatedHairstyles);
      }),
      catchError(error => {
        this.#toastrService.error('Failed to delete service. Please try again.', 'Error');
        throw error;
      }),
      finalize(() => this.#isDeleting.set(false))
    );
  }

  UpdateHairstyle(formData: any, id: number): Observable<any> {
    this.#isSaving.set(true);
    return this.#http.put(`${this.#apiBaseUrl}/tools/coiffure/${id}`, formData).pipe(
      tap(() => {
        this.#toastrService.success('Service updated successfully!', 'Success');
      }),
      catchError(error => {
        this.#toastrService.error('Failed to update service. Please try again.', 'Error');
        throw error;
      }),
      finalize(() => this.#isSaving.set(false))
    );
  }

  refreshHairstyles(): void {
    this.getHairstyles().subscribe();
  }

  getHairstyleById(id: number): Observable<IHairstyle> {
    this.#isLoading.set(true);
    return this.#http.get<IHairstyle>(`${this.#apiBaseUrl}/hairstyles/${id}`).pipe(
      tap((res: IHairstyle) => {
        this.#hairstyle.set(res);
      }),
      catchError((error: any) => {
        this.#toastrService.error('Failed to load hairstyle details with ID', 'Error');
        throw error;
      }),
      finalize(() => this.#isLoading.set(false))
    );
  }

  getHairstyleByName(name: string): Observable<IHairstyle> {
    this.#isLoading.set(true);
    return this.#http.get<IHairstyle>(`${this.#apiBaseUrl}/hairstyles/${name}`).pipe(
      tap((res: IHairstyle) => {
        this.#hairstyle.set(res);
      }),
      catchError((error: any) => {
        this.#toastrService.error('Failed to load hairstyle details', 'Error');
        throw error;
      }),
      finalize(() => this.#isLoading.set(false))
    );
  }

  resetLoadingStates(): void {
    this.#isLoading.set(false);
    this.#isSaving.set(false);
    this.#isDeleting.set(false);
  }

}
